/**
 * Cloudflare Turnstile Bot Protection Widget.
 *
 * Privacy-preserving, non-intrusive CAPTCHA alternative.
 * If VITE_CLOUDFLARE_TURNSTILE_SITE_KEY is not configured (e.g. local dev, tests),
 * this component renders nothing and gracefully succeeds.
 */

import { useEffect, useRef } from 'react'

import { env } from '@/app/env'

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement | string,
        options: {
          sitekey: string
          callback?: (token: string) => void
          'error-callback'?: () => void
          'expired-callback'?: () => void
          theme?: 'auto' | 'light' | 'dark'
          size?: 'normal' | 'flexible' | 'compact'
          action?: string
        },
      ) => string
      remove: (widgetId: string) => void
      reset: (widgetId: string) => void
    }
    onloadTurnstileCallback?: () => void
  }
}

interface TurnstileProps {
  onVerify: (token: string) => void
  onExpire?: () => void
  onError?: () => void
  action?: string
  className?: string
}

let scriptLoadingPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve()
  if (window.turnstile) return Promise.resolve()
  if (scriptLoadingPromise) return scriptLoadingPromise

  scriptLoadingPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[src*="turnstile"]')
    if (existing) {
      if (window.turnstile) {
        resolve()
      } else {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', (e) => reject(e))
      }
      return
    }

    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = (e) => reject(e)
    document.head.appendChild(script)
  })

  return scriptLoadingPromise
}

export function Turnstile({ onVerify, onExpire, onError, action = 'enterprise_lead', className = '' }: TurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const widgetIdRef = useRef<string | null>(null)
  const siteKey = env.turnstileSiteKey

  useEffect(() => {
    if (!siteKey) {
      onVerify('')
      return undefined
    }

    let isMounted = true

    loadTurnstileScript()
      .then(() => {
        if (!isMounted || !containerRef.current || !window.turnstile) return

        // Clean up any previous widget instance
        if (widgetIdRef.current) {
          try {
            window.turnstile.remove(widgetIdRef.current)
          } catch {
            // ignore
          }
        }

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          size: 'flexible',
          callback: (token: string) => {
            if (isMounted) onVerify(token)
          },
          'expired-callback': () => {
            if (isMounted) onExpire?.()
          },
          'error-callback': () => {
            if (isMounted) onError?.()
          },
        })
      })
      .catch(() => {
        if (isMounted) onError?.()
      })

    return () => {
      isMounted = false
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current)
        } catch {
          // ignore
        }
        widgetIdRef.current = null
      }
    }
  }, [siteKey, action, onVerify, onExpire, onError])

  if (!siteKey) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={`cf-turnstile-wrap ${className}`}
      style={{ minHeight: '42px', margin: 'var(--s2) 0' }}
    />
  )
}
